import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  FiUser,
  FiActivity,
  FiSettings,
  FiShield,
  FiMail,
  FiPhone,
  FiMapPin,
  FiCalendar,
  FiEdit3,
  FiClock,
  FiCheck,
  FiX,
  FiAlertCircle,
  FiSave,
  FiLogIn,
  FiLock,
} from 'react-icons/fi';
import { FaIndianRupeeSign } from 'react-icons/fa6';
import { useAuth } from '../../provider/auth.provider';
import axiosInstance from '../../libs/axios';

// Types based on your AuthContext
interface Permission {
  id: number;
  name: string;
  resource: string;
  action: string;
}

interface RolePermission {
  permission_id: number;
  permission: Permission;
}

interface User {
  id: number;
  email: string;
  username: string;
  profile_picture: string;
  first_name: string;
  last_name: string;
  role: {
    id: number;
    name: string;
    level: number;
    role_permissions: RolePermission[];
  };
  phone_number?: string | null;
  country_code?: string | null;
  date_of_birth?: string | null;
  gender?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  pincode?: string | null;
  status?: string;
  created_at?: string;
}

interface TabProps {
  user: User;
}

// Personal Information Tab Component
const PersonalInfoTab: React.FC<TabProps> = ({ user }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(user?.profile_picture || null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { fetchProfile } = useAuth();

  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone_number: user?.phone_number || '',
    country_code: user?.country_code || '',
    date_of_birth: user?.date_of_birth || '',
    gender: user?.gender || '',
    address: user?.address || '',
    city: user?.city || '',
    country: user?.country || '',
    pincode: user?.pincode || ''
  });

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      const formDataToSend = new FormData();

      // Append all text fields
      Object.entries(formData).forEach(([key, value]) => {
        if (value) {
          formDataToSend.append(key, value);
        }
      });

      // Append file if selected
      if (selectedFile) {
        formDataToSend.append('profile_picture', selectedFile);
      }

      await axiosInstance.put('/auth/update-profile', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setIsEditing(false);
      fetchProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      phone_number: user?.phone_number || '',
      country_code: user?.country_code || '',
      date_of_birth: user?.date_of_birth || '',
      gender: user?.gender || '',
      address: user?.address || '',
      city: user?.city || '',
      country: user?.country || '',
      pincode: user?.pincode || ''
    });
    setImagePreview(user?.profile_picture || null);
    setSelectedFile(null);
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-4 py-2 text-blue-800 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
            >
              <FiEdit3 size={16} />
              Edit
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancel}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                <FiX size={16} />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 text-white bg-blue-800 rounded hover:bg-blue-900 transition-colors disabled:opacity-50"
              >
                <FiSave size={16} />
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              {isEditing ? (
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                  <FiUser className="text-gray-400" size={18} />
                  <span className="text-gray-900">{user?.first_name || 'Not provided'}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              {isEditing ? (
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                  <FiUser className="text-gray-400" size={18} />
                  <span className="text-gray-900">{user?.last_name || 'Not provided'}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                <FiMail className="text-gray-400" size={18} />
                <span className="text-gray-900">{user?.email}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country Code</label>
              {isEditing ? (
                <input
                  type="text"
                  name="country_code"
                  value={formData.country_code}
                  onChange={handleInputChange}
                  placeholder="+1"
                  className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                  <FiPhone className="text-gray-400" size={18} />
                  <span className="text-gray-900">{user?.country_code || 'Not provided'}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              {isEditing ? (
                <input
                  type="text"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                  <FiPhone className="text-gray-400" size={18} />
                  <span className="text-gray-900">
                    {user?.country_code && user?.phone_number
                      ? `${user?.country_code} ${user?.phone_number}`
                      : 'Not provided'}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
              {isEditing ? (
                <input
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                  <FiCalendar className="text-gray-400" size={18} />
                  <span className="text-gray-900">
                    {user?.date_of_birth ? new Date(user?.date_of_birth).toLocaleDateString() : 'Not provided'}
                  </span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              {isEditing ? (
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                  <FiUser className="text-gray-400" size={18} />
                  <span className="text-gray-900">{user?.gender || 'Not provided'}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              {isEditing ? (
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded">
                  <FiMapPin className="text-gray-400 mt-0.5" size={18} />
                  <div className="text-gray-900">
                    {user?.address ? (
                      <div>
                        <div>{user?.address}</div>
                        {(user?.city || user?.state || user?.country || user?.pincode) && (
                          <div className="text-sm text-gray-600 mt-1">
                            {[user?.city, user?.state, user?.country, user?.pincode].filter(Boolean).join(', ')}
                          </div>
                        )}
                      </div>
                    ) : (
                      'Not provided'
                    )}
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              {isEditing ? (
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                  <FiMapPin className="text-gray-400" size={18} />
                  <span className="text-gray-900">{user?.city || 'Not provided'}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              {isEditing ? (
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                  <FiMapPin className="text-gray-400" size={18} />
                  <span className="text-gray-900">{user?.country || 'Not provided'}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
              {isEditing ? (
                <input
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                  <FiMapPin className="text-gray-400" size={18} />
                  <span className="text-gray-900">{user?.pincode || 'Not provided'}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                <div className={`w-3 h-3 rounded-full ${user?.status === 'active' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <span className="text-gray-900 capitalize">{user?.status || 'Unknown'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


interface Activity {
  id: string;
  user_id: number;
  entity: string;
  entity_id: string | null;
  action: string;
  message: string;
  prev_data: any;
  modified_data: any;
  meta_data: {
    url: string;
    method: string;
    timestamp: string;
    ip_address: string;
    user_agent: string;
  };
  created_at: string;
  updated_at: string;
}

interface PaginationData {
  offset: number;
  limit: number;
  page: number;
  total_pages: number;
  total_count: number;
  has_next_page: boolean;
  has_prev_page: boolean;
  next_page: number | null;
  prev_page: number | null;
}

// Activities Tab Component
const ActivitiesTab: React.FC<TabProps> = ({ user }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const observerTarget = useRef<HTMLDivElement>(null);

  const fetchActivities = async (page: number = 1) => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get(`/logs/${user.id}/user`, {
        params: { page, limit: 10 }
      });

      if (response.data.success) {
        const newActivities = response.data.data.logs;
        setActivities(prev => page === 1 ? newActivities : [...prev, ...newActivities]);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities(1);
  }, [user?.id]);

  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const [target] = entries;
    if (target.isIntersecting && pagination?.has_next_page && !loading) {
      fetchActivities(pagination.next_page!);
    }
  }, [pagination, loading]);

  useEffect(() => {
    const element = observerTarget.current;
    const option = { threshold: 0 };
    const observer = new IntersectionObserver(handleObserver, option);

    if (element) observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, [handleObserver]);

  const getActivityIcon = (entity: string, action: string) => {
    if (entity === 'auth' && action === 'login') {
      return <FiLogIn className="text-blue-600" size={18} />;
    }
    switch (entity) {
      case 'user':
        return <FiUser className="text-orange-600" size={18} />;
      case 'auth':
        return <FiShield className="text-green-600" size={18} />;
      case 'cab_hiring':
      case 'cab_visit':
        return <FiActivity className="text-purple-600" size={18} />;
      case 'attendance':
        return <FiCheck className="text-green-600" size={18} />;
      default:
        return <FiEdit3 className="text-blue-600" size={18} />;
    }
  };

  const getStatusIcon = (action: string) => {
    switch (action) {
      case 'create':
      case 'login':
        return <FiCheck className="text-green-500" size={16} />;
      case 'update':
        return <FiEdit3 className="text-blue-500" size={16} />;
      case 'delete':
        return <FiX className="text-red-500" size={16} />;
      case 'read':
        return <FiClock className="text-gray-500" size={16} />;
      default:
        return <FiActivity className="text-gray-500" size={16} />;
    }
  };

  if (initialLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Recent Activities</h3>

        <div className="space-y-4">
          {activities.map((activity) => (
            <div key={activity.id} className="flex items-start gap-4 p-4 border border-gray-100 rounded hover:bg-gray-50 transition-colors">
              <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                {getActivityIcon(activity.entity, activity.action)}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-medium text-gray-900 capitalize">
                    {activity.entity.replace('_', ' ')} - {activity.action}
                  </h4>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(activity.action)}
                    <span className="text-xs text-gray-500">
                      {new Date(activity.created_at).toLocaleDateString()} at {new Date(activity.created_at).toLocaleTimeString()}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-600">{activity.message}</p>
                {activity.meta_data && (
                  <div className="mt-2 text-xs text-gray-500">
                    <span className="font-medium">{activity.meta_data.method}</span> {activity.meta_data.url}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {loading && (
          <div className="mt-6 text-center py-4">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        )}

        <div ref={observerTarget} className="h-4"></div>

        {!pagination?.has_next_page && activities.length > 0 && (
          <div className="mt-6 text-center text-sm text-gray-500">
            No more activities to load
          </div>
        )}

        {activities.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500">
            No activities found
          </div>
        )}
      </div>
    </div>
  );
};

// Permissions Tab Component
const PermissionsTab: React.FC<TabProps> = ({ user }) => {
  const permissions = user?.role.role_permissions || [];

  const groupedPermissions = permissions.reduce((acc, rolePermission) => {
    const { resource } = rolePermission.permission;
    if (!acc[resource]) {
      acc[resource] = [];
    }
    acc[resource].push(rolePermission.permission);
    return acc;
  }, {} as Record<string, Permission[]>);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4 p-4p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Role & Permissions</h3>
            <p className="text-sm text-gray-600 mt-1">Current role and associated permissions</p>
          </div>
          <div className="text-right">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              <FiShield size={14} className="mr-1" />
              {/* {user?.role.name === 'Technical Assistant' ? 'Technical Associate' : user?.role.name} */}
              {user?.role.name}
            </div>
            <div className="text-xs text-gray-500 mt-1">Level {user?.role.level}</div>
          </div>
        </div>

        <div className="space-y-4">
          {Object.entries(groupedPermissions).map(([resource, resourcePermissions]) => (
            <div key={resource} className="border border-gray-200 rounded p-4">
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <FiShield size={16} className="text-blue-600" />
                {resource}
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {resourcePermissions.map((permission) => (
                  <div
                    key={permission.id}
                    className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-800 rounded text-sm"
                  >
                    <FiCheck size={14} />
                    <span className="capitalize">{permission.action}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {permissions.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <FiAlertCircle size={24} className="mx-auto mb-2" />
            <p>No permissions assigned to this role</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Settings Tab Component
const SettingsTab: React.FC<TabProps> = ({ user }) => {
  const [notifications, setNotifications] = useState({
    email: true,
    push: false,
    sms: false
  });

  const [privacy, setPrivacy] = useState({
    profileVisible: true,
    showEmail: false,
    showPhone: false
  });

  return (
    <div className="space-y-6">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4 p-4p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Account Settings</h3>

        <div className="space-y-8">
          {/* Notification Settings */}
          <div>
            <h4 className="font-medium text-gray-900 mb-4">Notification Preferences</h4>
            <div className="space-y-3">
              {Object.entries(notifications).map(([key, value]) => (
                <label key={key} className="flex items-center justify-between p-3 border border-gray-200 rounded">
                  <span className="text-sm text-gray-700 capitalize">{key} Notifications</span>
                  <button
                    onClick={() => setNotifications(prev => ({ ...prev, [key]: !value }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${value ? 'bg-blue-800' : 'bg-gray-300'
                      }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value ? 'translate-x-6' : 'translate-x-1'
                        }`}
                    />
                  </button>
                </label>
              ))}
            </div>
          </div>

          {/* Privacy Settings */}
          <div>
            <h4 className="font-medium text-gray-900 mb-4">Privacy Settings</h4>
            <div className="space-y-3">
              {Object.entries(privacy).map(([key, value]) => (
                <label key={key} className="flex items-center justify-between p-3 border border-gray-200 rounded">
                  <span className="text-sm text-gray-700">
                    {key === 'profileVisible' ? 'Profile Visible to Others' :
                      key === 'showEmail' ? 'Show Email in Profile' :
                        'Show Phone in Profile'}
                  </span>
                  <button
                    onClick={() => setPrivacy(prev => ({ ...prev, [key]: !value }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${value ? 'bg-blue-800' : 'bg-gray-300'
                      }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value ? 'translate-x-6' : 'translate-x-1'
                        }`}
                    />
                  </button>
                </label>
              ))}
            </div>
          </div>

          {/* Account Actions */}
          <div>
            <h4 className="font-medium text-gray-900 mb-4">Account Actions</h4>
            <div className="space-y-3">
              <button className="w-full text-left p-3 border border-gray-200 rounded hover:bg-gray-50 transition-colors">
                <div className="text-sm font-medium text-gray-900">Change Password</div>
                <div className="text-xs text-gray-500">Update your account password</div>
              </button>
              <button className="w-full text-left p-3 border border-gray-200 rounded hover:bg-gray-50 transition-colors">
                <div className="text-sm font-medium text-gray-900">Two-Factor Authentication</div>
                <div className="text-xs text-gray-500">Enable additional security for your account</div>
              </button>
              <button className="w-full text-left p-3 border border-red-200 rounded hover:bg-red-50 transition-colors text-red-600">
                <div className="text-sm font-medium">Delete Account</div>
                <div className="text-xs text-red-500">Permanently delete your account and all data</div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Change Password Tab Component
const ChangePasswordTab: React.FC<TabProps> = ({ user }) => {
  const [formData, setFormData] = useState({
    password: '',
    new_password: '',
    confirm_new_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (formData.new_password !== formData.confirm_new_password) {
      setError("New passwords don't match");
      return;
    }

    if (formData.new_password.length < 6) {
      setError("New password must be at least 6 characters long");
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.put('/auth/change-password', {
        current_password: formData.password,
        new_password: formData.new_password
      });
      setSuccess('Password changed successfully');
      setFormData({
        password: '',
        new_password: '',
        confirm_new_password: ''
      });
    } catch (err: any) {
      console.error('Error changing password:', err);
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Change Password</h3>
        
        <form onSubmit={handleSubmit} className="max-w-md space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded text-sm flex items-center gap-2">
              <FiAlertCircle size={16} />
              {error}
            </div>
          )}
          
          {success && (
            <div className="p-3 bg-green-50 text-green-700 rounded text-sm flex items-center gap-2">
              <FiCheck size={16} />
              {success}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input
              type="password"
              name="new_password"
              value={formData.new_password}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <input
              type="password"
              name="confirm_new_password"
              value={formData.confirm_new_password}
              onChange={handleChange}
              required
              className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="flex items-center justify-center gap-2 px-6 py-2.5 text-white bg-blue-800 rounded hover:bg-blue-900 transition-colors disabled:opacity-50 w-full sm:w-auto"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Updating...
                </>
              ) : (
                <>
                  <FiSave size={16} />
                  Update Password
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Bank & Payment Tab Component
const BankPaymentTab: React.FC<TabProps> = ({ user }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [qrPreview, setQrPreview] = useState<string | null>(null);
  const [qrFile, setQrFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    bank_name: '',
    bank_account_number: '',
    ifsc_code: '',
  });

  useEffect(() => {
    if (!user?.id) return;
    setFetchLoading(true);
    axiosInstance.get(`/user-details/${user.id}/details`)
      .then(r => {
        const d = r.data?.data;
        if (d) {
          setFormData({
            bank_name: d.bank_name || '',
            bank_account_number: d.bank_account_number || '',
            ifsc_code: d.ifsc_code || '',
          });
          if (d.payment_qr_code) setQrPreview(d.payment_qr_code);
        }
      })
      .catch(() => {})
      .finally(() => setFetchLoading(false));
  }, [user?.id]);

  const handleCancel = () => {
    setQrFile(null);
    setIsEditing(false);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      if (formData.bank_name) fd.append('bank_name', formData.bank_name);
      if (formData.bank_account_number) fd.append('bank_account_number', formData.bank_account_number);
      if (formData.ifsc_code) fd.append('ifsc_code', formData.ifsc_code);
      if (qrFile) fd.append('payment_qr_code', qrFile);
      await axiosInstance.put(`/user-details/${user.id}/details`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Bank details updated');
      setQrFile(null);
      setIsEditing(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update bank details');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Bank & Payment</h3>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-4 py-2 text-blue-800 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
            >
              <FiEdit3 size={16} /> Edit
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancel}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                <FiX size={16} /> Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 text-white bg-blue-800 rounded hover:bg-blue-900 transition-colors disabled:opacity-50"
              >
                <FiSave size={16} /> {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.bank_name}
                  onChange={e => setFormData(p => ({ ...p, bank_name: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. State Bank of India"
                />
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                  <FaIndianRupeeSign className="text-gray-400" size={18} />
                  <span className="text-gray-900">{formData.bank_name || 'Not provided'}</span>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.bank_account_number}
                  onChange={e => setFormData(p => ({ ...p, bank_account_number: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Bank account number"
                />
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                  <FaIndianRupeeSign className="text-gray-400" size={18} />
                  <span className="text-gray-900">{formData.bank_account_number || 'Not provided'}</span>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">IFSC Code</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.ifsc_code}
                  onChange={e => setFormData(p => ({ ...p, ifsc_code: e.target.value.toUpperCase() }))}
                  className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. SBIN0001234"
                />
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded">
                  <FaIndianRupeeSign className="text-gray-400" size={18} />
                  <span className="text-gray-900 font-mono">{formData.ifsc_code || 'Not provided'}</span>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment QR Code</label>
            <div className="border border-gray-200 rounded p-4 space-y-3">
              {(qrPreview) ? (
                <div className="flex justify-center">
                  <img
                    src={qrFile ? URL.createObjectURL(qrFile) : qrPreview}
                    alt="Payment QR Code"
                    className="w-40 h-40 object-contain border border-gray-200 rounded"
                  />
                </div>
              ) : (
                <div className="flex justify-center items-center w-40 h-40 mx-auto border border-dashed border-gray-300 rounded bg-gray-50">
                  <span className="text-xs text-gray-400 text-center">No QR code uploaded</span>
                </div>
              )}
              {isEditing && (
                <div>
                  <input
                    type="file"
                    id="qr-upload"
                    accept="image/*"
                    className="hidden"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) { setQrFile(file); }
                    }}
                  />
                  <label
                    htmlFor="qr-upload"
                    className="block w-full text-center px-4 py-2 border border-blue-300 text-blue-700 rounded cursor-pointer hover:bg-blue-50 text-sm"
                  >
                    {qrFile ? qrFile.name : 'Upload QR Image'}
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main UserProfile Component
const UserProfile = () => {
  const [activeTab, setActiveTab] = useState('personal');
  const { user, fetchProfile } = useAuth();
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user?.profile_picture) {
      setImagePreview(user.profile_picture);
    }
  }, [user]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveImage = async () => {
    if (!selectedFile) return;
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);

      await axiosInstance.put('/auth/update-profile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      await fetchProfile();
      setSelectedFile(null);
    } catch (error) {
      console.error('Error updating profile picture:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleCancelImage = () => {
    setSelectedFile(null);
    setImagePreview(user?.profile_picture || null);
  };

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: FiUser },
    { id: 'activities', label: 'Activities', icon: FiActivity },
    { id: 'permissions', label: 'Permissions', icon: FiShield },
    { id: 'bank', label: 'Bank & Payment', icon: FaIndianRupeeSign },
    { id: 'change-password', label: 'Change Password', icon: FiLock },
    // { id: 'settings', label: 'Settings', icon: FiSettings },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'personal':
        return <PersonalInfoTab user={user as User} />;
      case 'activities':
        return <ActivitiesTab user={user as User} />;
      case 'permissions':
        return <PermissionsTab user={user as User} />;
      case 'bank':
        return <BankPaymentTab user={user as User} />;
      case 'change-password':
        return <ChangePasswordTab user={user as User} />;
      case 'settings':
        return <SettingsTab user={user as User} />;
      default:
        return <PersonalInfoTab user={user as User} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Profile Header */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <div className="relative group">
            <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-100 shadow-sm bg-gray-50 flex items-center justify-center">
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-800 to-blue-600 text-white text-2xl font-bold">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
              )}
            </div>

            <input
              type="file"
              id="header-profile-upload"
              className="hidden"
              accept="image/*"
              onChange={handleImageChange}
              disabled={uploading}
            />
            
            {!selectedFile && (
              <label
                htmlFor="header-profile-upload"
                className="absolute bottom-0 right-0 bg-white text-gray-700 p-1.5 rounded-full cursor-pointer hover:bg-gray-50 transition-colors shadow-md border border-gray-200"
                title="Change Profile Picture"
              >
                <FiEdit3 size={12} />
              </label>
            )}

            {selectedFile && (
              <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 bg-white p-1 rounded shadow-lg z-10">
                <button
                  onClick={handleSaveImage}
                  disabled={uploading}
                  className="p-1 text-green-600 hover:bg-green-50 rounded"
                  title="Save"
                >
                  <FiCheck size={16} />
                </button>
                <button
                  onClick={handleCancelImage}
                  disabled={uploading}
                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                  title="Cancel"
                >
                  <FiX size={16} />
                </button>
              </div>
            )}
          </div>

          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">
              {user?.first_name} {user?.last_name}
            </h1>
            <p className="text-gray-600 mb-2">@{user?.username}</p>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <FiMail size={14} />
                {user?.email}
              </div>
              <div className="flex items-center gap-1">
                <FiShield size={14} />
                {/* {user?.role.name === 'Technical Assistant' ? 'Technical Associate' : user?.role.name} */}
                {user?.role.name}
              </div>
              {user?.created_at && (
                <div className="flex items-center gap-1">
                  <FiCalendar size={14} />
                  Member since {new Date(user?.created_at).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id
                    ? 'border-blue-800 text-blue-800'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};

export default UserProfile;